import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CloudUpload,
  X,
  Loader2,
  FileText,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import type { UploadedDocument, IncidentType } from '../types';
import type { ClaimTheme } from '../constants';
import { AnimatedButton } from '../shared/AnimatedButton';
import { cn } from '@/lib/utils';
import {
  getDocumentRequirements,
  getHardRequirements,
  getSoftRequirements,
} from '../constants';
import { areHardRequirementsMet } from '../useClaimState';
import { RequirementChecklist } from '../floating/RequirementChecklist';

interface DocumentationSectionProps {
  documents: UploadedDocument[];
  incidentType: IncidentType | null;
  onUpload: (file: File, forRequirement?: string) => void;
  onRemove: (id: string) => void;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
  theme?: ClaimTheme;
}

export function DocumentationSection({
  documents,
  incidentType,
  onUpload,
  onRemove,
  onContinue,
  onSkip,
  onBack,
  theme = 'invol',
}: DocumentationSectionProps) {
  // Generic upload (no specific requirement)
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => onUpload(file));
    },
    [onUpload]
  );

  // Upload for a specific requirement
  const handleUploadForRequirement = useCallback(
    (file: File, requirementId: string) => {
      onUpload(file, requirementId);
    },
    [onUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
    },
    maxSize: 32 * 1024 * 1024, // 32MB
  });

  const uploadingCount = documents.filter((d) => d.isUploading).length;
  const requirements = getDocumentRequirements(incidentType);
  const hardReqs = getHardRequirements(incidentType);
  const softReqs = getSoftRequirements(incidentType);
  const allHardMet = areHardRequirementsMet(incidentType, documents);
  const hasHardRequirements = hardReqs.length > 0;

  // For "other" incident type or no incident type, use free-form upload
  const useFreeFormUpload = requirements.useFreeFormUpload || !incidentType;

  return (
    <motion.div
      key="documentation"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col"
    >
      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onBack}
        className="self-start -ml-2 mb-4 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
      </motion.button>

      {/* Header */}
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-3xl font-bold text-slate-900 mb-2"
      >
        {useFreeFormUpload ? 'Belege hochladen' : 'Beweisdokumente hochladen'}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-slate-500 mb-6"
      >
        {useFreeFormUpload
          ? 'Verspätungs-E-Mails, Stornierungsbenachrichtigungen, Bordkarten...'
          : 'Laden Sie die benötigten Nachweise für Ihren Fall hoch'}
      </motion.p>

      {/* Requirements Checklists (only for specific incident types) */}
      {!useFreeFormUpload && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="mb-6 space-y-5"
        >
          {/* Progress indicator */}
          <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
            {allHardMet ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-green-700">
                  Alle erforderlichen Dokumente hochgeladen
                </span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-medium text-amber-700">
                  Erforderliche Dokumente fehlen noch
                </span>
              </>
            )}
          </div>

          {/* Hard Requirements */}
          {hardReqs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide">
                  Erforderlich
                </h3>
              </div>
              <RequirementChecklist
                requirements={hardReqs}
                documents={documents}
                level="hard"
                onUploadForRequirement={handleUploadForRequirement}
              />
            </div>
          )}

          {/* Soft Requirements */}
          {softReqs.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
                Empfohlen (optional)
              </h3>
              <RequirementChecklist
                requirements={softReqs}
                documents={documents}
                level="soft"
                onUploadForRequirement={handleUploadForRequirement}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Other documents upload zone */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {!useFreeFormUpload && (
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
            Sonstige Dokumente
          </h3>
        )}
        <div
          {...getRootProps()}
          className={cn(
            'relative rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all mb-4',
            isDragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
          )}
        >
          <input {...getInputProps()} />
          <CloudUpload
            className={cn(
              'w-8 h-8 mx-auto mb-2',
              isDragActive ? 'text-blue-500' : 'text-slate-400'
            )}
          />
          <p className="text-sm text-slate-600">
            {isDragActive ? (
              'Dateien hier ablegen...'
            ) : (
              <>
                {useFreeFormUpload
                  ? 'Dateien per Drag & Drop ablegen oder '
                  : 'Weitere Dokumente per Drag & Drop oder '}
                <span className="text-blue-500 hover:text-blue-600">
                  durchsuchen
                </span>
              </>
            )}
          </p>
          <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG bis zu 32MB</p>
        </div>
      </motion.div>

      {/* Document list */}
      <AnimatePresence mode="popLayout">
        {documents.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2 mb-6 max-h-40 overflow-y-auto"
          >
            {documents.map((doc, index) => (
              <motion.div
                key={doc.id}
                layout
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg bg-white border',
                  doc.error ? 'border-red-200' : 'border-slate-200'
                )}
              >
                <div
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                    doc.error ? 'bg-red-100' : 'bg-slate-100'
                  )}
                >
                  <FileText
                    className={cn(
                      'w-4 h-4',
                      doc.error ? 'text-red-500' : 'text-slate-500'
                    )}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-sm font-medium truncate',
                      doc.error ? 'text-red-700' : 'text-slate-700'
                    )}
                  >
                    {doc.name}
                  </p>
                  {doc.category && !doc.error && (
                    <p className="text-xs text-green-600">
                      {doc.category.replace(/_/g, ' ')}
                    </p>
                  )}
                  {doc.error && (
                    <p className="text-xs text-red-500">{doc.error}</p>
                  )}
                </div>
                {doc.isUploading ? (
                  <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(doc.id);
                    }}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="flex items-center justify-between pt-4"
      >
        {/* Skip button - only show if no hard requirements or all are met */}
        {!hasHardRequirements || allHardMet ? (
          <button
            onClick={onSkip}
            className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            {documents.length > 0
              ? 'Weiter ohne weitere Dokumente'
              : 'Überspringen'}
          </button>
        ) : (
          <div className="text-sm text-red-500 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            Erforderliche Dokumente fehlen
          </div>
        )}

        <AnimatedButton
          variant="primary"
          size="lg"
          onClick={onContinue}
          disabled={uploadingCount > 0 || (hasHardRequirements && !allHardMet)}
          loading={uploadingCount > 0}
          icon={<ArrowRight className="w-5 h-5" />}
          theme={theme}
        >
          Weiter
        </AnimatedButton>
      </motion.div>
    </motion.div>
  );
}
